// Note to actions (plan workstream 6, Phase 2): paste a meeting note, get proposals for
// tasks, compliance date updates, the next review date and the RoA scope. Rule-based and
// pure, so nothing leaves the tenancy and every proposal shows the line it came from.
// Nothing is applied until the adviser ticks it.

export type ComplianceField = 'lastRiskProfileDate' | 'lastFactFindDate' | 'lastFeeConsentDate' | 'lastBestInterestDate';

export const COMPLIANCE_FIELD_LABEL: Record<ComplianceField, string> = {
  lastRiskProfileDate: 'Risk profile',
  lastFactFindDate: 'Fact find',
  lastFeeConsentDate: 'Fee consent',
  lastBestInterestDate: 'Best interests check',
};

export interface ProposedTask { text: string; owner: string | null; due: string | null; source: string }
export interface ProposedCompliance { field: ComplianceField; date: string; source: string }
export interface NoteProposal {
  tasks: ProposedTask[];
  compliance: ProposedCompliance[];
  reviewDue: { date: string; source: string } | null;
  roaScope: string[];
}

const COMPLIANCE_RULES: Array<{ field: ComplianceField; subject: RegExp }> = [
  { field: 'lastRiskProfileDate', subject: /\brisk\s*(profile|profiling|questionnaire|tolerance)\b/i },
  { field: 'lastFactFindDate', subject: /\bfact[\s-]?find\b/i },
  { field: 'lastFeeConsentDate', subject: /\b(fee consent|consent form|ongoing fee (consent|renewal)|ofa (consent|renewal))\b/i },
  { field: 'lastBestInterestDate', subject: /\bbest[\s-]interests?\b/i },
];
const DONE = /\b(completed?|updated?|done|reviewed|signed|received|confirmed|re-?done|finali[sz]ed)\b/i;
const NOT_DONE = /\b(not|still to|pending|outstanding|to be|needs?|need to|will|next time|awaiting|chase)\b/i;
const ADVICE = /\b(recommend(ed|s)?|agreed to|we agreed|decided to|switch|rollover|roll over|sell|buy|rebalance|contribut(e|ion)|salary sacrifice|consolidat(e|ion)|start (a |an )?pension|commute|withdraw)\b/i;
const TASK_LEAD = /^\s*(?:[-*•]\s*)?(?:\[\s?\]\s*)?(?:action|todo|to do|follow[\s-]?up|task)\s*[:-]\s*/i;
const BULLET = /^\s*(?:[-*•]|\d+[.)]|\[\s?\])\s+/;
const SECTION = /^\s*(actions?|next steps?|to ?dos?|follow[\s-]?ups?)\s*:?\s*$/i;
const OTHER_HEADING = /^\s*[A-Z][A-Za-z ]{2,30}:\s*$/;
const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

const pad = (n: number) => String(n).padStart(2, '0');
function addDays(iso: string, d: number): string {
  const t = new Date(`${iso}T00:00:00Z`); t.setUTCDate(t.getUTCDate() + d); return t.toISOString().slice(0, 10);
}
function addMonths(iso: string, m: number): string {
  const [y, mo, d] = iso.split('-').map(Number);
  const total = y * 12 + (mo - 1) + m;
  const ny = Math.floor(total / 12); const nm = total % 12;
  const last = new Date(Date.UTC(ny, nm + 1, 0)).getUTCDate();
  return `${ny}-${pad(nm + 1)}-${pad(Math.min(d, last))}`;
}

/** A due date from text: "by 15/11", "by 15/11/2026", "next week", "in 3 weeks", "in 2 months", "by March". */
export function parseDue(text: string, today: string): string | null {
  const dmy = /\b(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?\b/.exec(text);
  if (dmy) {
    const d = Number(dmy[1]); const m = Number(dmy[2]);
    if (d >= 1 && d <= 31 && m >= 1 && m <= 12) {
      let y = dmy[3] ? Number(dmy[3].length === 2 ? `20${dmy[3]}` : dmy[3]) : Number(today.slice(0, 4));
      let iso = `${y}-${pad(m)}-${pad(d)}`;
      if (!dmy[3] && iso < today) { y += 1; iso = `${y}-${pad(m)}-${pad(d)}`; }
      return iso;
    }
  }
  if (/\b(tomorrow)\b/i.test(text)) return addDays(today, 1);
  if (/\bnext week\b/i.test(text)) return addDays(today, 7);
  if (/\bnext month\b/i.test(text)) return addMonths(today, 1);
  const rel = /\bin (\d{1,2}|a|one|two|three|six) (day|week|month|year)s?\b/i.exec(text);
  if (rel) {
    const words: Record<string, number> = { a: 1, one: 1, two: 2, three: 3, six: 6 };
    const n = words[rel[1].toLowerCase()] ?? Number(rel[1]);
    const unit = rel[2].toLowerCase();
    return unit === 'day' ? addDays(today, n) : unit === 'week' ? addDays(today, 7 * n) : addMonths(today, unit === 'year' ? 12 * n : n);
  }
  const mon = /\b(?:by|in|before|end of)\s+(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\b/i.exec(text);
  if (mon) {
    const m = MONTHS.indexOf(mon[1].toLowerCase()) + 1;
    let y = Number(today.slice(0, 4));
    const last = (yy: number) => new Date(Date.UTC(yy, m, 0)).getUTCDate();
    if (`${y}-${pad(m)}-${pad(last(y))}` < today) y += 1;
    return `${y}-${pad(m)}-${pad(last(y))}`;
  }
  return null;
}

function owner(text: string): { owner: string | null; text: string } {
  const paren = /\s*\((?:owner:\s*)?([A-Z][a-z]+)\)\s*$/.exec(text);
  if (paren) return { owner: paren[1], text: text.slice(0, paren.index).trim() };
  const lead = /^([A-Z][a-z]+)\s+to\s+(.+)$/.exec(text);
  if (lead && !['Need', 'Needs', 'Client', 'Clients', 'We', 'I'].includes(lead[1])) return { owner: lead[1], text: lead[2].trim() };
  return { owner: null, text };
}

const sentences = (note: string) => note.split(/\n+/).flatMap((l) => l.split(/(?<=[.!?])\s+(?=[A-Z])/)).map((s) => s.trim()).filter(Boolean);

export function proposeActions(note: string, today: string): NoteProposal {
  const lines = note.split(/\r?\n/);
  const tasks: ProposedTask[] = [];
  let inSection = false;
  let sectionHits = 0;
  for (const raw of lines) {
    const line = raw.trim();
    // A blank line closes an actions section once it has items (a gap straight after the heading does not).
    if (!line) { if (sectionHits) inSection = false; continue; }
    if (SECTION.test(line)) { inSection = true; sectionHits = 0; continue; }
    if (OTHER_HEADING.test(line)) { inSection = false; continue; }
    let body: string | null = null;
    if (TASK_LEAD.test(line)) body = line.replace(TASK_LEAD, '');
    else if (inSection) { body = line.replace(BULLET, ''); sectionHits++; }
    if (!body || body.length < 3) continue;
    const o = owner(body.replace(/\s+/g, ' '));
    tasks.push({ text: o.text.replace(/[.;]$/, '').slice(0, 200), owner: o.owner, due: parseDue(o.text, today), source: line.slice(0, 200) });
  }

  const compliance: ProposedCompliance[] = [];
  const roaScope: string[] = [];
  let reviewDue: NoteProposal['reviewDue'] = null;
  for (const s of sentences(note)) {
    for (const r of COMPLIANCE_RULES) {
      if (r.subject.test(s) && DONE.test(s) && !NOT_DONE.test(s) && !compliance.some((c) => c.field === r.field)) {
        compliance.push({ field: r.field, date: today, source: s.slice(0, 200) });
      }
    }
    if (!reviewDue && /\b(next |annual )?review\b/i.test(s) && /\b(in \d|in (a|one|six|twelve) |next year|in \w+ months?|by |on \d)/i.test(s)) {
      const twelve = /\btwelve months\b/i.test(s) ? addMonths(today, 12) : null;
      const date = twelve ?? (/\bnext year\b/i.test(s) ? addMonths(today, 12) : parseDue(s, today));
      if (date && date > today) reviewDue = { date, source: s.slice(0, 200) };
    }
    if (ADVICE.test(s) && !TASK_LEAD.test(s) && roaScope.length < 8) roaScope.push(s.replace(BULLET, '').slice(0, 300));
  }
  return { tasks: tasks.slice(0, 20), compliance, reviewDue, roaScope };
}
